import * as Q from "q";
import { WebPageDataService } from "VSS/Contributions/Services";
import * as Performance from "VSS/Performance";
import { FeatureManagementService } from "VSS/FeatureManagement/Services";
import { getService } from "VSS/Service";

import * as VisitsContracts from "CodeReview/Visits/Contracts";
import * as VisitsClient from "CodeReview/Visits/RestClient";
import { ProjectCollection } from "Presentation/Scripts/TFS/TFS.OM.Common";
import { ViewedStatus } from "VersionControl/Scenarios/PullRequestDetail/Stores/ViewedStore";
import { CodeHubContributionIds } from "VersionControl/Scripts/CodeHubContributionIds";
import * as CustomerIntelligenceConstants from "VersionControl/Scripts/CustomerIntelligenceConstants";
import { GitRepositoryContext } from "VersionControl/Scripts/GitRepositoryContext";
import { PullRequestArtifact } from "VersionControl/Scripts/PullRequestArtifact";
import { CachedSource } from "VersionControl/Scripts/Sources/Source";
import { onClickNavigationHandler } from "VersionControl/Scripts/Utils/XhrNavigationUtils";
import { getCommitUrl, getBranchCompareFilesUrl } from "VersionControl/Scripts/VersionControlUrls";

interface UpdateLastVisitResult {
    lastVisit: Date;
    viewedState: string;
}

export class NavigationSource extends CachedSource {
    private static DATA_ISLAND_PROVIDER_ID: string = "ms.vss-code-web.pull-request-detail-visit-data-provider";
    private static DATA_ISLAND_CACHE_PREFIX: string = "TFS.VersionControl.PullRequestDetailVisitProvider";
    private static DATA_ISLAND_CACHE_SUFFIX: string = "Visit";

    private _visitsClient: VisitsClient.VisitsHttpClient;

    constructor(private _repositoryContext: GitRepositoryContext, private _pullRequestId: number) {
        super(NavigationSource.DATA_ISLAND_PROVIDER_ID, NavigationSource.DATA_ISLAND_CACHE_PREFIX);

        this._visitsClient = ProjectCollection.getDefaultConnection().getHttpClient<VisitsClient.VisitsHttpClient>(VisitsClient.VisitsHttpClient);
    }

    /**
     * Update the last visit data for this user and return the current visit data
     */
    public updateLastVisitAsync(): IPromise<UpdateLastVisitResult> {
        if (!this._pullRequestId) {
            return Q(null); // pull request id could be invalid
        }

        const scenario = Performance.getScenarioManager().startScenario(
            CustomerIntelligenceConstants.VERSION_CONTROL_AREA,
            CustomerIntelligenceConstants.PULL_REQUEST_UPDATE_VISIT_FEATURE
        );

        scenario.addData({
            pullRequestId: this._pullRequestId
        });

        // check for cached value before going to REST
        const cached = this.fromCache<VisitsContracts.ArtifactVisit>(
            NavigationSource.DATA_ISLAND_CACHE_SUFFIX,
            VisitsContracts.TypeInfo.ArtifactVisit
        );

        if (cached) {
            scenario.addData({ cached: true });
            scenario.end();
            return Q<UpdateLastVisitResult>({
                lastVisit: cached.previousLastVisitedDate || null,
                viewedState: cached.viewedState
            });
        }

        const pullRequestArtifact = new PullRequestArtifact({
            projectGuid: this._repositoryContext.getProjectId(),
            repositoryId: this._repositoryContext.getRepositoryId(),
            pullRequestId: this._pullRequestId
        });

        const visit = { artifactId: pullRequestArtifact.getUri() } as VisitsContracts.ArtifactVisit;

        const deferred = Q.defer<UpdateLastVisitResult>();

        this._visitsClient.updateLastVisit(visit).then(updatedVisit => {
            deferred.resolve({
                lastVisit: updatedVisit.previousLastVisitedDate || null,
                viewedState: updatedVisit.viewedState
            });
        });

        return deferred.promise;
    }

    public updateViewedState(hashes: string[], viewedStatus: ViewedStatus): IPromise<void> {
        if (!hashes || !hashes.length) {
            return;
        }

        return new Promise((resolve, reject) => {
            const properties = {
                pullRequestId: this._pullRequestId,
                modifyHashes: hashes,
                modifyViewedStatus: viewedStatus,
            };

            const webPageDataService = getService(WebPageDataService);
            webPageDataService.reloadCachedProviderData(
                NavigationSource.DATA_ISLAND_PROVIDER_ID,
                resolve,
                properties,
                reject);
        });
    }

    /**
     * Navigates to the page of the provided commit.
     */
    public viewCommit(commitId: string, baseCommitId: string, event: React.MouseEvent<HTMLElement>): void {
        let url: string;
        if (baseCommitId) {
            const baseVersion = "GC" + baseCommitId;
            const targetVersion = "GC" + commitId;
            url = getBranchCompareFilesUrl(this._repositoryContext, baseVersion, targetVersion);
        } else {
            url = getCommitUrl(this._repositoryContext, commitId);
        }

        const hub = "ms.vss-code-web.commits-hub";

        onClickNavigationHandler(event, hub, url);
    }
}
